import { parseWithZod } from '@conform-to/zod'
import {
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import {
  isRouteErrorResponse,
  useActionData,
  useLoaderData,
  useRouteError,
} from '@remix-run/react'
import { useEffect, useState } from 'react'
import { RecentlyViewedService } from '#app/entities/car/recentlyViewedService.ts'
import { CarService } from '#app/entities/car/service.server.js'
import { ContactService } from '#app/entities/contact/service.server.ts'
import CarCertificationEmail from '#app/features/email/carCertificationEmail.tsx'
import { NotFoundPage } from '#app/routes/$.tsx'
import { getCarSlugMetaTags } from '#app/shared/lib/meta-tags.ts'
import {
  isCarOlderThan6Years,
  invariantResponse,
} from '#app/shared/lib/misc.js'
import CarDescription from '#app/shared/ui/data-display/carDescription/CarDescription.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import Header from '#app/shared/ui/layout/Header.js'
import { ContactTypeEnum } from '../../../../types/enums'
import CheckModal from './ui/checkModal'
import { DetailsFormSchema } from './ui/detailsForm'
import DetailsLeft from './ui/detailsLeft'
import DetailsModal from './ui/detailsModal'
import DetailsRight from './ui/detailsRight'

type CarImage = {
  id: string
  isSignOfUse: boolean
  isPreviewImage: boolean
}

type ProcessedImageIds = {
  signOfUseImageIds: string[]
  regularImageIds: string[]
}

function processCarImages(car: any): ProcessedImageIds {
  const nonPreviewImages: CarImage[] = car.images.filter(
    (image: CarImage) => !image.isPreviewImage,
  )

  return nonPreviewImages.reduce<ProcessedImageIds>(
    (result, image) => {
      if (image.isSignOfUse) {
        result.signOfUseImageIds.push(image.id)
      } else {
        result.regularImageIds.push(image.id)
      }
      return result
    },
    {
      signOfUseImageIds: [],
      regularImageIds: [],
    },
  )
}

export async function loader({ params, request }: LoaderFunctionArgs) {
  const carSlug = params.carSlug

  if (!carSlug) {
    throw new Response('Car slug is required', {
      status: 400,
    })
  }

  const car = await CarService.findBySlug(carSlug)

  // For SEO purposes, we need to return a 410 if the car is sold, and a 404 if the car is not found.
  if (!car) {
    const exists = await CarService.existsBySlug(carSlug)
    if (exists) {
      throw new Response('Car is sold', { status: 410 })
    }
    throw new Response('Car not found', { status: 404 })
  }

  RecentlyViewedService.addCarSlug(car.slug)

  try {
    await CarService.registerVisit(car.id)
    console.log('Visit successfully registered for:', car.id)
  } catch (error) {
    console.error('Error registering visit in loader:', error)
  }
  // const similarCarPrices = await CarService.findSimilarCarPrices({
  //   condition: car.condition,
  //   brandId: car.brandId,
  //   model: car.model,
  //   year: car.year,
  //   id: car.id,
  // })

  return {
    car,
    financingAvailable: !isCarOlderThan6Years(car),
    //similarCarPrices,
    cleanUrl: request.url,
    domainUrl: new URL(request.url).origin,
  }
}

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  return getCarSlugMetaTags({
    car: data?.car as any,
    domainUrl: data?.domainUrl,
    matches,
  })
}

// Make car not found page prettier
export function ErrorBoundary() {
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    if (error.status === 404 || error.status === 410) {
      return <NotFoundPage />
    }
  }

  throw error
}

export async function action({ params, request }: ActionFunctionArgs) {
  const carSLug = params.carSlug
  invariantResponse(carSLug, 'Car slug is required', { status: 400 })
  const car = await CarService.findBySlug(carSLug)
  invariantResponse(car, 'Car not found', {
    status: 404,
  })
  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: DetailsFormSchema,
  })

  if (submission.status !== 'success') {
    return Response.json(
      { result: submission.reply(), status: 'error' },
      { status: 400 },
    )
  }
  const { name, email, fullPhone, postalCode } = submission.value
  const response = await ContactService.create({
    name,
    email,
    phoneNumber: fullPhone,
    postalCode,
    type: ContactTypeEnum.CERTIFICATION,
  })
  if (response.success) {
    try {
      await CarCertificationEmail({
        name,
        postalCode,
        email,
        fullPhone,
        message: 'Solicitud de certificación de vehículo',
        carName: car.brand.name,
        carModel: car.model,
        carDescription: car.description ?? '',
        matriculation: car.matriculation,
      })
    } catch (error) {
      console.error('Error sending car certification email:', error)
    }
    return Response.json({
      status: 'success',
      message: 'Contacto certificación creado',
    })
  }
  return null
}

export default function Index() {
  const {
    //similarCarPrices, Commented to use in the future
    car,
    financingAvailable,
  } = useLoaderData<typeof loader>()
  const actionData = useActionData()

  const [similarCars, setSimilarCars] = useState<any[]>([])

  useEffect(() => {
    const fetchSimilarCars = () => {
      fetch(`/api/cars/${car.slug}/similar`)
        .then(async (response) => {
          if (response.ok) {
            const data = (await response.json()) as any[]
            setSimilarCars(data)
          }
        })
        .catch((error) => {
          console.error('Error fetching similar cars:', error)
        })
    }

    fetchSimilarCars()
  }, [car.id])

  const { regularImageIds, signOfUseImageIds } = processCarImages(car)

  return (
    <>
      {/* HEADER */}
      <Header isSticky={true} showBanner={true} />

      <div
        className={`
          bg-neutral-25

          lg:bg-neutral-25 lg:px-4
        `}
      >
        <div
          className={`
            items-start justify-center gap-3

            lg:flex
          `}
        >
          <DetailsLeft
            car={car as any}
            financingAvailable={financingAvailable}
            // similarCarPrices={similarCarPrices as any}
            actionData={actionData as any}
            regularImageIds={regularImageIds}
            signOfUseImageIds={signOfUseImageIds}
          />
          <DetailsRight
            car={car as any}
            financingAvailable={financingAvailable}
          />
        </div>

        <Spacer size="40px" />
        <Spacer size="16px" />
        <CarDescription
          name="También te puede interesar"
          cars={similarCars as any}
          className={`
            px-4

            xl:px-0
          `}
        />
        <Spacer size="40px" />
      </div>
      <DetailsModal />
      <CheckModal />
    </>
  )
}
